import * as React from 'react';

import styled from 'styled-components';

import { COLOR, FONT_SIZE, SPACING } from '../../packages/constants';
import { HelperModal, HelperModalProps, QuestionMark } from '../helper-modal';

export interface CheckboxInputProps {
    /**
     *  Data-test-id
     */
    dataId: string;
    /**
     * Field label
     */
    label: string;
    id: string;
    name: string;
    /**
     * Sets the input to focus on page load
     */
    autofocus?: boolean;
    checked?: boolean;
    disabled?: boolean;
    /**
     * Shows the (?) icon
     */
    hasHelper?: boolean;
    /**
     * Content for the helper icon
     */
    helperContent?: React.ReactNode;
    helperProps?: HelperModalProps;
    inline?: boolean;
    invalid?: boolean;
    onChange?: (e: any) => void;
    /**
     * Makes the label thin
     */
    thinLabel?: boolean;
    className?: string;
    labelMaxWidth?: string;
    labelOverflow?: string;
    labelTextOverflow?: string;
}

interface StyledTextLabelProps {
    disabled?: boolean;
    invalid?: boolean;
    inline?: boolean;
    thinLabel?: boolean;
    labelMaxWidth?: string;
    labelOverflow?: string;
    labelTextOverflow?: string;
}

interface WrapperLabelProps {
    inline?: boolean;
}

const Wrapper = styled.label<WrapperLabelProps>`
    &&& {
        display: inline-flex;
        flex-direction: ${(props: WrapperLabelProps): string => (props.inline ? 'row-reverse' : 'column')};
        align-items: ${(props: WrapperLabelProps): string => (props.inline ? 'center' : '')};
        justify-content: ${(props: WrapperLabelProps): string => (props.inline ? 'flex-end' : '')};
    }
`;

const WrapperHasHelper = styled.div<WrapperLabelProps>`
    display: inline-flex;
    flex-direction: ${(props: WrapperLabelProps): string => (props.inline ? 'row-reverse' : 'column')};
    align-items: ${(props: WrapperLabelProps): string => (props.inline ? 'center' : '')};
    justify-content: ${(props: WrapperLabelProps): string => (props.inline ? 'flex-end' : '')};
`;

const StyledLabelWrapper = styled.div`
    display: inline-flex;
`;

const StyledTextLabel = styled.span<StyledTextLabelProps>`
    display: block;
    margin-left: ${(props: StyledTextLabelProps): string => (props.inline ? SPACING.X_SMALL : '0')};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
    max-width: ${(props: StyledTextLabelProps): string => (props.labelMaxWidth ? props.labelMaxWidth : 'none')};
    overflow: ${(props: StyledTextLabelProps): string => (props.labelOverflow ? props.labelOverflow : 'visible')};
    text-overflow: ${(props: StyledTextLabelProps): string =>
        props.labelTextOverflow ? props.labelTextOverflow : 'clip'};
`;

const StyledLabelHasHelper = styled.label<StyledTextLabelProps>`
    && {
        display: inline-block;
        margin-right: ${SPACING.X_SMALL};
        margin-left: ${(props: StyledTextLabelProps): string => (props.inline ? SPACING.X_SMALL : '0')};
        font-size: ${FONT_SIZE.SMALL};
        line-height: ${FONT_SIZE.MEDIUM};
        font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
        color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
        opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
        max-width: ${(props: StyledTextLabelProps): string => (props.labelMaxWidth ? props.labelMaxWidth : 'none')};
        overflow: ${(props: StyledTextLabelProps): string => (props.labelOverflow ? props.labelOverflow : 'visible')};
        text-overflow: ${(props: StyledTextLabelProps): string =>
            props.labelTextOverflow ? props.labelTextOverflow : 'clip'};
    }
`;

const StyledCheckbox = styled.input``;

export const CheckboxInput = React.forwardRef<HTMLInputElement, CheckboxInputProps>(
    (
        {
            onChange,
            dataId,
            label,
            id,
            hasHelper,
            helperContent,
            helperProps,
            name,
            autofocus,
            inline,
            thinLabel,
            checked,
            disabled,
            invalid,
            className,
            labelMaxWidth,
            labelOverflow,
            labelTextOverflow,
            ...props
        },
        ref,
    ) => {
        return hasHelper ? (
            <WrapperHasHelper inline={inline} className={className} {...props}>
                <StyledLabelWrapper>
                    <StyledLabelHasHelper
                        data-test-id={`${dataId}-label`}
                        disabled={disabled}
                        inline={inline}
                        thinLabel={thinLabel}
                        invalid={invalid}
                        htmlFor={id}
                        labelMaxWidth={labelMaxWidth}
                        labelOverflow={labelOverflow}
                        labelTextOverflow={labelTextOverflow}
                        title={label}
                    >
                        {label}
                    </StyledLabelHasHelper>
                    <HelperModal dataId={dataId} modalContent={helperContent} {...helperProps}>
                        <QuestionMark />
                    </HelperModal>
                </StyledLabelWrapper>
                <StyledCheckbox
                    type="checkbox"
                    data-test-id={`${dataId}-input`}
                    id={id}
                    autoFocus={autofocus}
                    name={name}
                    disabled={disabled}
                    checked={checked}
                    onChange={onChange}
                    ref={ref}
                />
            </WrapperHasHelper>
        ) : (
            <Wrapper data-test-id={`${dataId}-label`} inline={inline} className={className} {...props}>
                <StyledTextLabel
                    inline={inline}
                    thinLabel={thinLabel}
                    invalid={invalid}
                    disabled={disabled}
                    labelMaxWidth={labelMaxWidth}
                    labelOverflow={labelOverflow}
                    labelTextOverflow={labelTextOverflow}
                    title={label}
                >
                    {label}
                </StyledTextLabel>
                <StyledCheckbox
                    type="checkbox"
                    data-test-id={`${dataId}-input`}
                    id={id}
                    autoFocus={autofocus}
                    name={name}
                    disabled={disabled}
                    checked={checked}
                    onChange={onChange}
                    ref={ref}
                />
            </Wrapper>
        );
    },
);
CheckboxInput.displayName = 'CheckboxInput';
